<script setup lang="ts">
  import {
    AppVersion,
    AppVersionTone,
    FontIcon,
    Illustration,
  } from '@tundr/ds-components';
  import { useLocale } from '@tundr/i18n';
  import tundrLogoWhite from '@tundr/theme-assets/images/tundr-logo-white.svg?url';
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import { computed } from 'vue';
  import { useWizardStore } from '../../store/wizard-stepper.store';
  import {
    bottomCtaClass,
    checkIconClass,
    connectorClass,
    dividerClass,
    footerClass,
    headerClass,
    indicatorClass,
    indicatorVariants,
    itemClass,
    labelClass,
    labelVariants,
    listClass,
    navClass,
    poweredByClass,
    poweredByLogoClass,
    stepButtonClass,
    wrapperClass,
  } from './WizardSidebar.css';
  import { WizardState, type WizardSidebarProps } from './WizardSidebar.types';

  const props = defineProps<WizardSidebarProps>();

  const { t } = useLocale('wizard-stepper');

  const stepperStore = useWizardStore();

  const visibleSteps = computed(() =>
    (stepperStore.steppers[props.namespace]?.steps ?? [])
      .map((step, index) => ({ step, index }))
      .filter(({ step }) => !step.isRecap),
  );

  const activeIndex = computed(
    () => stepperStore.steppers[props.namespace]?.activeIndex ?? 0,
  );

  const maxReachedIndex = computed(
    () => stepperStore.steppers[props.namespace]?.maxReachedIndex ?? 0,
  );

  const isWizardCompleted = computed(
    () => stepperStore.steppers[props.namespace]?.completed ?? false,
  );

  const isRecapStep = computed(() => {
    const stepper = stepperStore.steppers[props.namespace];
    return stepper?.steps[stepper.activeIndex]?.isRecap ?? false;
  });

  const getState = (index: number): WizardState => {
    if (isWizardCompleted.value && index <= maxReachedIndex.value) {
      return WizardState.Completed;
    }
    if (index === activeIndex.value) {
      return WizardState.Current;
    }
    if (index < activeIndex.value) {
      return WizardState.Completed;
    }
    return WizardState.Upcoming;
  };

  const isReachable = (index: number): boolean =>
    index <= maxReachedIndex.value;

  const getStepAriaLabel = (index: number, label: string) =>
    t('step_aria_label', {
      number: index + 1,
      label,
      status: t(`status.${getState(index)}`),
    });

  const goToStep = (index: number, name: string) => {
    if (
      !stepperStore.steppers[props.namespace] ||
      isWizardCompleted.value ||
      index === activeIndex.value ||
      !isReachable(index)
    ) {
      return;
    }
    stepperStore.goToStep(props.namespace, name);
  };
</script>

<template>
  <div :class="wrapperClass" :data-testid="testId">
    <div v-if="$slots.header" :class="headerClass">
      <slot name="header" />
    </div>
    <div v-if="$slots.header" :class="dividerClass" />
    <div v-if="$slots['top-cta']">
      <slot name="top-cta" />
    </div>
    <nav :class="navClass" :aria-label="t('aria_label')">
      <ol :class="listClass">
        <li
          v-for="({ step, index }, position) in visibleSteps"
          :key="step.name"
          :class="itemClass"
        >
          <div v-if="position > 0" :class="connectorClass" aria-hidden="true" />
          <button
            type="button"
            :class="stepButtonClass"
            :disabled="isWizardCompleted || !isReachable(index)"
            :aria-label="getStepAriaLabel(index, step.label)"
            :aria-current="
              index === activeIndex ? 'step' : undefined
            "
            :data-testid="testId ? `${testId}-step-${step.name}` : undefined"
            @click="goToStep(index, step.name)"
          >
            <span :class="[indicatorClass, indicatorVariants[getState(index)]]">
              <FontIcon
                v-if="getState(index) === WizardState.Completed"
                :icon-name="TUNDR_ICONS.CHECK"
                :class="checkIconClass"
              />
              <template v-else>{{ index + 1 }}</template>
            </span>
            <span :class="[labelClass, labelVariants[getState(index)]]">
              {{ step.label }}
            </span>
          </button>
        </li>
      </ol>
    </nav>
    <div
      v-if="$slots['bottom-cta'] && !isWizardCompleted && !isRecapStep"
      :class="bottomCtaClass"
    >
      <slot name="bottom-cta" />
    </div>
    <div :class="footerClass">
      <div :class="poweredByClass">
        <span>{{ t('powered_by') }}</span>
        <Illustration
          :class="poweredByLogoClass"
          :src="tundrLogoWhite"
          alt="Tundr"
          :width="72"
          :height="18"
        />
      </div>
      <AppVersion :tone="AppVersionTone.OnBrand" />
    </div>
  </div>
</template>
